<template>
    <div class="page page-layout template">
        <div class="flexbox-fullheight form-view page-container">
            <header class="page-header">
                <div class="page-header-background"></div>

                <template v-if="!loading">
                    <router-link class="return-to-dashboard" :to="{ name: 'dashboard', hash: '#templates' }">
                        <icon class="arrow-left-circle-svg large-svg fill-white stroke-white" name="arrow-left" />
                    </router-link>

                    <div class="grid-x">
                        <div class="cell large-8">
                            <nav class="page-breadcrumbs">
                                <ol class="page-breadcrumbs-list" role=list>
                                    <li class="page-breadcrumbs-item"><router-link class="template-navigation-item" :to="{ name: 'dashboard', hash: '#templates' }">Oversigt</router-link></li>
                                    <li class="page-breadcrumbs-item" aria-current="page">
                                        <icon class="small-svg fill-white" name="chevron-right"></icon>
                                        <icon class="small-svg fill-white" name="template"></icon>
                                        Skabelon
                                    </li>
                                </ol>
                            </nav>

                            <h1 class="page-heading">
                                <template v-if="!taskTemplate.taskId">Ny skabelon</template>
                                <template v-else>{{ taskTemplate.name }}</template>
                            </h1>

                            <div class="flexbox align-items flexbox-wrap" style="gap: 6px 18px;">
                                <div class="flexbox-center page-header-info">
                                    <icon class="medium-svg fill-white" name="user-reverse" />
                                    <select class="select-options" v-model="taskTemplate.ownerUserId">
                                        <option :value="null">Ingen ansvarlig</option>
                                        <option v-for="responsible in organizationUserList" :selected="responsible.userId" :key="responsible.userId" :value="responsible.userId">{{ responsible.name }}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div class="cell large-2 page-header-actions">
                            <base-split-button @click="save()">
                                <template v-slot:default>{{ saveStatus }}</template>
                                <template v-if="validRemoveTemplate" v-slot:list>
                                    <ul role="menu">
                                        <li role="none">
                                            <button tabindex="0" role="menuitem" @click="removeTemplate()">Slet</button>
                                        </li>
                                    </ul>
                                </template>
                            </base-split-button>
                        </div>
                        <div class="cell large-2 page-header-actions">
                            <span class="save-time-stamp" v-if="savedTimestamp !== null">Gemt {{ savedTimestamp | formatTime }}</span>
                        </div>
                    </div>
                </template>
            </header>

            <main class="page-content" v-show="!loading">
                <guide-nav :icons="iconObj" :initStep="step" @changeStep="changedStep" />

                <section v-if="step === 0">
                    <h2>Oplysninger</h2>

                    <div class="form">
                        <div class="grid-x">
                            <div class="flexbox-center">
                                <icon class="file-text small-label-svg" name="file-text" />
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                <label for="template-name">Skabelonnavn<span class="required"></span></label>
                            </div>
                            <div class="auto cell flexbox-start">
                                <input type="text" id="template-name" v-model="taskTemplate.name" placeholder="Indtast navn" maxlength="255">
                            </div>
                        </div>

                        <hr />

                        <div class="grid-x">
                            <div class="flexbox-center">
                                <icon name="file-text" class="file-text small-label-svg" />
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                <label for="template-name">Rekvirer ved oprettelse</label>
                            </div>
                            <div class="auto cell flexbox-start">
                                <switches theme="custom" type-bold="false" color="blue" v-model="taskTemplate.templateCreateRequisition" />
                            </div>
                        </div>
                    </div>

                    <hr />

                    <supply-type :class="{'disable': taskTemplate.taskId || selectedTemplate.activities.length > 0 }" />
                </section>

                <!-- ------------- Documents ------------- -->
                <section v-if="step === 1">
                    <n-files :allowedFileTypes="applicationSettings.allowedFileTypes.join(',')" :showList="false" :uploadFunction="uploadFiles" :uploadStatus="uploadStatus" :uploadErrorResponse="uploadErrorResponse">
                        <template v-slot:heading>
                            Dokumenter
                        </template>
                        <template v-slot:description>
                            Upload dine billeder og dokumenter.<br />
                            <em>F.eks. *.pdf, *.jpg, *.doc, *.xls</em>
                        </template>

                        <template v-slot:upload-label>
                            <icon class="large-svg" name="upload" /><br />
                            <strong>Klik for at vælge dine filer</strong><br />
                            <small>... eller træk og slip dem her.</small>
                        </template>
                        <template v-slot:upload-title>
                            Klik for at vælge dine filer eller træk og slip dem her.
                        </template>
                        <template v-slot:upload-saving-singluar>
                            Uploader fil...
                        </template>
                        <template v-slot:upload-saving-plural>
                            Uploader flere filer...
                        </template>
                        <template v-slot:upload-success-singluar>
                            Fil uploadet.
                        </template>
                        <template v-slot:upload-success-plural>
                            Filer uploadet.
                        </template>
                        <template v-slot:upload-failed>
                            Upload fejlede desværre.
                        </template>
                    </n-files>

                    <p class="dfiles-list__empty" v-if="templateDocumentsSorted.length === 0">Der er ikke tilføjet nogle filer endnu.</p>

                    <template v-if="templateDocumentsSorted.length > 0">
                        <div class="document__options">
                            <div class="document__option">
                                <input type="checkbox" id="file-metadata" :checked="templateDocumentsMetadataVisible" @change="documentListMetadataToggle" />
                                <label for="file-metadata">Vis filoplysninger</label>
                            </div>
                        </div>

                        <ul class="document__list">
                            <li class="document__item" v-for="file in templateDocumentsSorted" :key="file.id">
                                <div class="document__image">
                                    <div class="document__loader"></div>
                                    <img v-if="file.contentType.startsWith('image')" :src="file.dataSource" alt="" loading="lazy" />
                                    <icon v-else :name="getIcon(file)" :class="getIcon(file)" />
                                    <button v-if="file.contentType.startsWith('image')" @click="documentOpen(file)"><span class="visually-hidden">Vis billede</span></button>
                                    <button v-else @click="documentDownload(file)"><span class="visually-hidden">Download fil</span></button>
                                    <div class="document__icon" v-if="file.description && !templateDocumentsMetadataVisible" :title="'Kommentar: ' + file.description">
                                        <icon name="info-2" />
                                    </div>
                                </div>
                                <div class="document__info" v-if="templateDocumentsMetadataVisible">
                                    <div class="document__name" :title="file.contentType.startsWith('image') ? $filters.formatDateTime(file.photoTaken) : file.name">
                                        <template v-if="file.contentType.startsWith('image')">{{ file.photoTaken | formatDateTime }}</template>
                                        <template v-else>{{ file.name }}</template>
                                    </div>
                                    <div class="document__date"><strong>Upload:</strong> <time :datetime="file.dateCreated">{{ file.dateCreated | formatDateShort }}</time></div>
                                    <div class="document__description" v-if="file.description"><strong>Kommentar:</strong> <span class="truncate" :title="file.description">{{ file.description }}</span></div>
                                </div>
                                <div class="document__actions">
                                    <button title="Tilføj kommentar" v-if="checkDescriptionAvailability(file)" @click="commentFile(file)">
                                        <span class="visually-hidden">Tilføj kommentar</span>
                                        <icon name="pencil" />
                                    </button>
                                    <button title="Download fil" @click="documentDownload(file)">
                                        <span class="visually-hidden">Download</span>
                                        <icon name="download-arrow" />
                                    </button>
                                    <button class="delete" title="Slet fil" @click="documentRemove(file.id)">
                                        <span class="visually-hidden">Slet</span>
                                        <icon name="trash" />
                                    </button>
                                </div>
                            </li>
                        </ul>
                    </template>
                </section>

                <!-- ------------- Aktiviteter ------------- -->
                <section v-if="step === 2">
                    <accordion-activities />
                </section>
            </main>
            <div v-show="loading" class="loading-div">
                <h1 class="loading-header">Henter skabelon...</h1>
            </div>
        </div>

        <base-map ref="baseMap" :locationWkt="'POINT EMPTY'" :supplyCode="selectedSupplyType.code" :supplyTypeId="selectedSupplyType.id" />
        <modal v-if="showModal" :type="modalType" :data="modalData" @close="closeModal" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import baseMap from './../components/shared/map/base-map.vue'
import NFiles from './../components/shared/files/files.vue'
import { wait } from '@/mixins/shared/helpers'
import changeTracking from '@/mixins/shared/change-tracking'
import guideNav from '@/components/shared/nav/guide-nav'
import supplyType from '@/components/shared/supplytype/supplytype'
import taskTemplateActivitiesVue from '@/components/pages/taskTemplate/taskTemplate-activities.vue'
import modal from '@/components/shared/modal/modal.vue'
import Switches from 'vue-switches'
import BaseSplitButton from '../components/base/BaseSplitButton.vue'

export default {
    name: 'taskTemplate',
    components: {
        modal,
        'base-map': baseMap,
        'NFiles': NFiles,
        'guide-nav': guideNav,
        'supply-type': supplyType,
        'accordion-activities': taskTemplateActivitiesVue,
        Switches,
        'base-split-button': BaseSplitButton
    },
    mixins: [changeTracking],
    data () {
        return {
            step: 0,
            dataPropertyName: 'taskTemplate', // Point to your data property
            taskTemplate: {
                activities: [],
                businessAreaTypeId: null,
                name: null,
                ownerUserId: null,
                taskId: null,
                taskVersionStamp: null,
                templateCreateRequisition: false
            },
            addressObject: {
                oneLineAddress: ''
            },
            existingTemplate: false,
            // loading
            loading: false,
            // save
            saveStatus: 'Gem',
            savedTimestamp: null,
            // modal
            showModal: false,
            modalType: null,
            modalData: null,
            // delete task
            taskWkt: '',
            removingTemplate: false,

            uploadStatusTimer: null,
            uploadStatus: 'initial',
            uploadErrorResponse: null,

            templateDocumentsMetadataVisible: true,
            sortKey: 'dateCreated',
            sortDirection: 'DESC'
        }
    },
    computed: {
        ...mapGetters({
            applicationSettings: 'getApplicationSettings',
            userId: 'getUserId',
            selectedSupplyType: 'selectedSupplyType',
            selectedTemplate: 'getSelectedTemplate',
            selectedActivities: 'getSelectedActivities',
            taskTemplateList: 'getTaskTemplateList',
            templateDocuments: 'getTaskDocuments',
            activitySaved: 'getActivitySaved',
            sysBusinessAreaTypeList: 'getSysBusinessAreaTypeList',
            organizationUserList: 'caseWorkerList'
        }),
        validRemoveTemplate () {
            if (this.taskTemplate && this.taskTemplate.taskId) {
                return true
            }
            return false
        },
        checkValidStatus () {
            let hide = this.selectedActivities.filter(p => p.selected).length > 0
            return hide
        },
        iconObj () {
            return [
                { icon: 'task', text: 'Oplysninger', active: true },
                { icon: 'pictures', text: 'Dokumenter', active: true, counter: this.templateDocuments.length > 0 ? this.templateDocuments.length : null },
                { icon: 'task-send', text: 'Aktiviteter', active: true }
            ]
        },

        templateDocumentsSorted () {
            return [...this.templateDocuments].sort((a, b) => {
                let modifier = 1

                if (this.sortDirection === 'DESC') {
                    modifier = -1
                }

                if (a[this.sortKey] < b[this.sortKey]) {
                    return -1 * modifier
                }
                if (a[this.sortKey] > b[this.sortKey]) {
                    return 1 * modifier
                }

                return 0
            })
        }
    },
    methods: {
        ...mapActions({
            clearTaskDocuments: 'clearTaskDocuments',
            getCurrentUserInfo: 'getCurrentUserInfo',
            getCurrentOrganizationList: 'getCurrentOrganizationList',
            getCaseworkers: 'getCaseworkers',
            getAllTypeLists: 'getAllTypeLists',
            getOrganizationList: 'getAssociatedOrganizationList',
            // other
            saveTemplate: 'createUpdateTaskTemplate',
            setSupplyType: 'setSelectedSupplyTypeFromId',
            getExistingTemplate: 'getWholeTaskTemplateItem',
            closeTask: 'setTaskStatusClose',
            openTask: 'setTaskStatusOpen',
            deleteTaskAndActivities: 'deleteTaskAndActivities',
            // is Template
            setTaskTemplate: 'setTaskTemplate',
            changeSelectedActivityList: 'changeSelectedActivityList',
            getTemplateList: 'getTaskTemplateList',
            // other
            clearSelectedTask: 'clearSelectedTask',

            // FILES
            documentAdd: 'taskDocumentAdd',
            documentRemove: 'taskDocumentRemove',
            documentOpen: 'taskDocumentOpen',
            documentDownload: 'taskDocumentDownload',
            documentDescriptionUpdate: 'taskDocumentDescriptionUpdate'
        }),
        removeTemplate () {
            this.removingTemplate = true
            this.openModal('handler', 'Sletter skabelon...')
            this.deleteTaskAndActivities(this.taskTemplate.taskId).then((response) => {
                if (response) {
                    this.$router.push({
                        name: 'dashboard'
                    })
                } else {
                    this.modalData = 'kunne ikke slette opgave'
                    this.removingTemplate = false
                    setTimeout(() => {
                        this.showModal = false
                    }, 1500)
                }
            }, (error) => {
                console.error('Remove template', error)
                this.modalData = 'kunne ikke slette opgave'
                this.removingTemplate = false
                setTimeout(() => {
                    this.showModal = false
                }, 1500)
            })
        },
        save () {
            if (this.saveStatus !== 'Gemmer') {
                this.saveStatus = 'Gemmer'
                this.taskTemplate.businessAreaTypeId = this.selectedSupplyType.id
                if (!this.taskTemplate.name) {
                    this.taskTemplate.name = 'Skabelon - ' + this.$filters.formatDateTime(new Date())
                }

                const payload = {
                    activities: this.selectedActivities,
                    taskId: this.taskTemplate.taskId,
                    taskVersionStamp: this.taskTemplate.taskVersionStamp,
                    businessAreaTypeId: this.selectedSupplyType.id,
                    name: this.taskTemplate.name,
                    ownerUserId: this.taskTemplate.ownerUserId,
                    templateCreateRequisition: this.taskTemplate.templateCreateRequisition
                }
                this.saveTemplate(payload).then(() => {
                    this.saveStatus = 'Gemt'
                    this.savedTimestamp = new Date()
                    setTimeout(() => {
                        this.saveStatus = 'Gem'
                    }, 3000)
                    this.taskTemplate.taskId = this.selectedTemplate.taskId
                    this.taskTemplate.taskVersionStamp = this.selectedTemplate.taskVersionStamp
                    this.taskTemplate.activities = this.selectedTemplate.activities
                    this.$store.state.task.activeTaskId = this.selectedTemplate.taskId

                    this.resetChanges()

                    if (!this.existingTemplate) {
                        this.existingTemplate = true

                        // Include ID in URL when saving a new template
                        this.$router.replace({
                            name: 'tasktemplateid',
                            params: {
                                id: this.taskTemplate.taskId
                            }
                        })
                    }
                }, error => {
                    this.saveStatus = 'Kunne ikke gemme'
                    setTimeout(() => {
                        this.saveStatus = 'Gem'
                    }, 3000)
                    console.error('Save template', error)
                })
            }
        },
        close () {
            let obj = {
                taskId: this.taskTemplate.taskId,
                taskVersionStamp: this.taskTemplate.taskVersionStamp
            }
            new Promise((resolve, reject) => {
                resolve(this.closeTask(obj))
            }).then((response) => {
                if (response.status === 200) {
                    this.taskTemplate.taskVersionStamp = response.data.taskVersionStamp
                    this.taskClosed = true
                }
            })
        },
        open () {
            let obj = {
                taskId: this.taskTemplate.taskId,
                taskVersionStamp: this.taskTemplate.taskVersionStamp
            }
            new Promise((resolve, reject) => {
                resolve(this.openTask(obj))
            }).then((response) => {
                if (response.status === 200) {
                    this.taskTemplate.taskVersionStamp = response.data.taskVersionStamp
                    this.taskClosed = false
                }
            })
        },
        openModal (type, data) {
            this.showModal = true
            this.modalType = type
            this.modalData = data
        },
        closeModal () {
            if (this.modalData.updating) {
                this.documentDescriptionUpdate(this.modalData)
            }

            this.showModal = false
        },
        changedStep (result) {
            this.step = result
        },
        onWktSelected (value) {
            this.taskTemplate.locationPointWkt = value
        },
        getExistingData () {
            return new Promise((resolve, reject) => {
                this.existingTemplate = true
                resolve(this.getExistingTemplate(this.$route.params.id))
            }).then(() => {
                this.$nextTick(() => {
                    const selected = Object.assign({}, this.$store.state.task.tempTask)
                    this.taskTemplate = selected
                    if (this.taskTemplate.businessAreaTypeId) {
                        // let foundSupplyTypeCode = this.sysBusinessAreaTypeList.find(areaType => areaType.sysBusinessAreaTypeId === this.taskTemplate.businessAreaTypeId).code
                        // this.$refs.taskmap.setSiteAndPage(foundSupplyTypeCode, this.onWktSelected, this.onLayerObject)
                    } else {
                        // this.$refs.taskmap.setSiteAndPage(this.selectedSupplyType.code, this.onWktSelected, this.onLayerObject)
                    }
                    // uses tempTask in Vuex to get this.task for validating save
                    // this.$store.state.task.tempTask = this.taskTemplate
                    this.$store.state.task.activeTaskId = this.taskTemplate.taskId
                    return true
                })
            }).then(() => {
                return this.setSupplyType(this.taskTemplate.businessAreaTypeId).then(() => {
                    this.selectedSupplyTypeId = this.$store.getters.selectedSupplyType.id
                    return this.selectedSupplyTypeId
                })
                    .then(() => {
                        return this.getTemplateList()
                    })
            })
        },
        initTemplate () {
            this.changeSelectedActivityList(true)
            this.$store.state.task.activityIndex = null
            this.$store.state.task.selectedTemplate = {name: null, activities: []}
            this.$store.state.task.removeActivities = []
            this.$store.state.task.wfsObjects = []

            return new Promise((resolve, reject) => {
                if (!this.$store.getters.userInfo.userId) {
                    resolve(this.getAllUserInfo())
                } else {
                    resolve(true)
                }
            }).then(() => {
                if (this.$route.params.id) {
                    return this.getExistingData()
                } else {
                    this.clearSelectedTask()
                    this.$store.state.task.selectedActivities = this.selectedTemplate.activities
                    this.$store.state.task.activeTaskId = ''
                }
                if (this.applicationSettings.uiSettings && this.applicationSettings.uiSettings.DefaultBusinessAreaTypeId !== null) {
                    this.setSupplyType(this.applicationSettings.uiSettings.DefaultBusinessAreaTypeId)
                }
            }).then(() => {
                this.initializeTracking(this.taskTemplate)
                this.loading = false
            })
        },
        getAllUserInfo () {
            let promise1 = this.getCurrentUserInfo()
            let promise2 = this.getCurrentOrganizationList()
            let promise3 = this.getAllTypeLists()

            Promise.all([promise1, promise2, promise3]).then((values) => {
                this.getOrganizationList()
                this.getCaseworkers()
                return true
            })
        },

        uploadFiles (element) {
            const startTime = performance.now()
            let endTime = startTime

            clearTimeout(this.uploadStatusTimer)
            this.uploadStatus = 'saving'

            Object.values(element.files).forEach((file) => {
                const objectData = {
                    data: new FormData(),
                    dataType: file.type,
                    file: file
                }

                objectData.data.append('content', file, file.name)

                this.documentAdd(objectData)
                    .then((result) => {
                        endTime = performance.now()

                        return result
                    })
                    .then(wait(250 - (endTime - startTime > 250 ? 250 : endTime - startTime))) // Make sure spinner is shown at least 250ms
                    .then(() => {
                        this.uploadStatus = 'success'
                        document.getElementById(this.$children.find(child => child.$options.name === 'NFiles').$children.find(child => child.inputId).inputId).value = ''
                        this.uploadStatusTimer = setTimeout(() => {
                            this.uploadStatus = 'initial'
                            this.save()
                        }, 3500)
                    })
                    .catch((error) => {
                        this.uploadErrorResponse = error
                        this.uploadStatus = 'failed'
                        this.uploadStatusTimer = setTimeout(() => {
                            this.uploadStatus = 'initial'
                        }, 5000)
                    })
            })
        },
        getIcon (file) {
            // Determine icon by mime type
            let iconName = 'unknown'

            if (file.contentType.indexOf('pdf') > -1) {
                iconName = 'pdf'
            } else if (file.contentType.indexOf('officedocument') > -1 && file.contentType.indexOf('.document') > -1) {
                iconName = 'doc'
            } else if (file.contentType.indexOf('officedocument') > -1 && file.contentType.indexOf('.presentation') > -1) {
                iconName = 'ppt'
            } else if (file.contentType.indexOf('officedocument') > -1 && file.contentType.indexOf('.sheet') > -1) {
                iconName = 'xls'
            } else if (file.contentType.indexOf('csv') > -1) {
                iconName = 'csv'
            } else if (file.contentType.indexOf('plain') > -1) {
                iconName = 'txt'
            } else if (file.contentType.indexOf('xml') > -1) {
                iconName = 'xml'
            }

            return iconName
        },
        checkDescriptionAvailability (file) {
            if (!file.description && this.hideDescription) {
                return false
            } else {
                return true
            }
        },
        commentFile (file) {
            this.openModal('document-modal-description', file)
        },

        documentListMetadataToggle () {
            this.templateDocumentsMetadataVisible = event.target.checked
            localStorage.setItem('templateDocumentsMetadataVisible', event.target.checked)
        }
    },
    created () {
        this.clearTaskDocuments()
        this.loading = true
        this.setTaskTemplate(true)
        this.initTemplate()
        if (localStorage.getItem('templateDocumentsMetadataVisible') === 'true') {
            this.templateDocumentsMetadataVisible = true
        }
    },
    beforeRouteLeave (to, from, next) {
        if (!this.removingTemplate && to.name !== 'tasktemplateid' && this.hasChanges()) {
            const answer = window.confirm('Du har ændringer, som ikke er gemt. Ønsker du at gemme?')
            if (answer) {
                this.openModal('handler', 'Gemmer...')
                this.save().then(() => next())
            } else {
                next()
            }
        } else {
            next()
        }
    }
}
</script>

<style lang="scss">
@import '../scss/utils/colors.scss';
@import '../scss/components/dfiles.scss';
@import '../scss/components/loader';
@import '../scss/pages/template';
@import '../scss/components/vue-switches';
</style>



// WEBPACK FOOTER //
// src/pages/taskTemplate.vue